import FileUploadController from "./file_upload";

export default class extends FileUploadController {
  static targets = [...FileUploadController.targets, "hiddenField"];

  connect() {
    super.connect();
    this.base64Array = [];
  }

  async updateFileList() {
    const newFiles = Array.from(this.fileTarget.files);

    if (this.maxFiles === 1) {
      this.filesArray = newFiles;
      this.base64Array = [];
      this.emptyFileList();
    } else {
      this.filesArray = this.filesArray.concat(newFiles);
    }

    const dataTransfer = new DataTransfer();
    this.filesArray.forEach((file) => dataTransfer.items.add(file));
    this.fileTarget.files = dataTransfer.files;

    this.listTarget.classList.remove("hidden");
    if (this.hasTemplateTarget) this.templateTarget.classList.remove("hidden");

    await this.populateFileItem(newFiles);

    if (this.hasTemplateTarget) this.templateTarget.classList.add("hidden");

    await this.validateFiles();
    this.toggleUploadButton();
  }

  async populateFileItem(files) {
    for (const file of files) {
      await this.addFile(file);
    }
    this.updateHiddenField();
  }

  addFile(file) {
    return new Promise((resolve) => {
      const clone = this.createFileItemElement(file);

      const reader = new FileReader();
      reader.onload = (e) => {
        const base64String = e.target.result;
        const fileImage = clone.querySelector(".file-image");

        if (file.type !== "application/pdf") {
          fileImage.src = base64String;
        }

        this.base64Array.push({
          name: file.name,
          base64: base64String,
          type: file.type,
          size: file.size
        });

        clone.dataset.fileId = file.name;
        this.listTarget.appendChild(clone);
        this.updateHiddenField();
        resolve();
      };

      reader.onerror = () => {
        console.error("Error reading file:", file.name);
        resolve();
      };

      reader.readAsDataURL(file);
    });
  }

  updateHiddenField() {
    if (!this.hasHiddenFieldTarget) return;

    if (this.maxFiles === 1 && this.base64Array.length > 0) {
      this.hiddenFieldTarget.value = this.base64Array[0].base64;
    } else if (this.base64Array.length > 0) {
      this.hiddenFieldTarget.value = JSON.stringify(this.base64Array);
    } else {
      this.hiddenFieldTarget.value = "";
    }
  }

  toggleUploadButton() {
    if (this.hasButtonTarget) {
      const hasFiles = this.base64Array.length > 0 ||
        (this.hasHiddenFieldTarget && this.hiddenFieldTarget.value);

      if (this.allFilesValid && hasFiles) {
        this.buttonTarget.disabled = false;
      } else {
        this.buttonTarget.disabled = true;
      }
    }
  }

  removeFile(event) {
    event.preventDefault();
    const fileId = event.currentTarget.dataset.fileId;
    const item = event.currentTarget.closest(".file-item");

    if (item) {
      item.remove();
    }

    if (fileId === "existing") {
      if (this.hasHiddenFieldTarget) {
        this.hiddenFieldTarget.value = "";
      }
      this.allFilesValid = false;
    } else {
      this.removeFileFromArrays(fileId);
    }

    const remainingFiles = this.listTarget.querySelectorAll(".file-item");
    if (remainingFiles.length === 0) {
      this.listTarget.classList.add("hidden");
    }

    this.toggleUploadButton();
  }

  removeFileFromArrays(fileName) {
    this.filesArray = this.filesArray.filter((f) => f.name !== fileName);
    this.base64Array = this.base64Array.filter((f) => f.name !== fileName);

    const dataTransfer = new DataTransfer();
    this.filesArray.forEach((file) => dataTransfer.items.add(file));
    this.fileTarget.files = dataTransfer.files;

    this.updateHiddenField();
  }

  removeAllFiles() {
    this.filesArray = [];
    this.base64Array = [];
    this.fileTarget.value = "";
    this.emptyFileList();
    this.listTarget.classList.add("hidden");
    this.updateHiddenField();
    this.validateFiles();
  }

  async upload(event) {
    event.preventDefault();

    const trigger = event.currentTarget;
    const url = trigger.dataset.url;

    if (!url) {
      this.showError("Upload URL not configured");
      return;
    }

    if (!this.hasHiddenFieldTarget || !this.hiddenFieldTarget.value) {
      this.showError("Please select a file first");
      return;
    }

    try {
      await this.sendUploadRequest(trigger);
      this.handleSuccess();
      this.dispatch("upload-success");
    } catch (error) {
      this.showError(error.message || "Upload failed");
      this.dispatch("upload-error", { detail: { error } });
    }
  }

  async sendUploadRequest(trigger) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    if (!csrfToken) throw new Error("CSRF token not found");

    const response = await fetch(trigger.dataset.url, {
      method: (trigger.dataset.method || "PATCH").toUpperCase(),
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
        "X-Requested-With": "XMLHttpRequest",
        "Accept": "application/json"
      },
      body: JSON.stringify({
        ...(trigger.dataset.urlParams && JSON.parse(trigger.dataset.urlParams)),
        data_content: {
          field_name: trigger.dataset.fieldName,
          field_value: this.hiddenFieldTarget.value
        }
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.errors?.join(", ") || `Upload failed (${response.status})`);
    }

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.errors?.join(", ") || "Upload failed");
    }

    return data;
  }

  showError(message) {
    if (this.hasGeneralErrorTarget) {
      this.wrapperTarget.classList.add("error");
      this.generalErrorTarget.textContent = message;
      this.generalErrorTarget.classList.remove("hidden");
    } else {
      alert(message);
    }
  }
}
